import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Loader2 } from "lucide-react";

export default function AdminCourseEdit() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [regen, setRegen] = useState({});  // module_id -> bool

  useEffect(() => {
    api.get(`/api/admin/courses/${courseId}`).then(({data}) => setCourse(data));
    api.get("/api/categories").then(({data}) => setCats(data));
  }, [courseId]);

  if (!course) return <div>Loading...</div>;

  const update = (k, v) => setCourse({...course, [k]: v});

  const regenThumb = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/admin/courses/${courseId}/regenerate-thumbnail`);
      update("thumbnail_url", data.thumbnail_url);
      toast.success("Thumbnail refreshed");
    } finally { setBusy(false); }
  };

  const regenQuiz = async (moduleId) => {
    setRegen((r) => ({...r, [moduleId]: true}));
    try {
      await api.post(`/api/admin/ai/regenerate-quiz/${courseId}/${moduleId}`, { num_questions: 5 });
      toast.success("Quiz regenerated");
      const { data } = await api.get(`/api/admin/courses/${courseId}`);
      setCourse(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quiz regen failed");
    } finally {
      setRegen((r) => ({...r, [moduleId]: false}));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/api/admin/courses/${courseId}`, {
        title: course.title, slug: course.slug, short_description: course.short_description,
        description: course.description, category_id: course.category_id,
        level: course.level, language: course.language,
        duration_hours: course.duration_hours, price_usd: course.price_usd,
        thumbnail_url: course.thumbnail_url, certificate_title: course.certificate_title,
        final_test_required: course.final_test_required, final_test_pass_mark: course.final_test_pass_mark,
        is_published: course.is_published, learning_outcomes: course.learning_outcomes,
      });
      setCourse(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-course-edit">
      <Link to="/admin/courses" className="text-sm text-[#D92D20] font-semibold">← All courses</Link>
      <h1 className="font-display font-black text-3xl mt-2">Edit course</h1>

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold">Title</label>
            <input data-testid="edit-title" value={course.title} onChange={(e) => update("title", e.target.value)}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Slug</label>
            <input data-testid="edit-slug" value={course.slug} onChange={(e) => update("slug", e.target.value)}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Category</label>
            <select data-testid="edit-category" value={course.category_id} onChange={(e) => update("category_id", e.target.value)}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Level</label>
            <select data-testid="edit-level" value={course.level} onChange={(e) => update("level", e.target.value)} className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
              <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Language</label>
            <select value={course.language} onChange={(e) => update("language", e.target.value)} className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
              <option>English</option><option>French</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Price (USD)</label>
            <input data-testid="edit-price" type="number" min={0} step={1} value={course.price_usd} onChange={(e) => update("price_usd", parseFloat(e.target.value))}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Duration (hours)</label>
            <input type="number" min={1} step={0.5} value={course.duration_hours} onChange={(e) => update("duration_hours", parseFloat(e.target.value))}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-semibold">Final test pass mark (%)</label>
            <input type="number" min={0} max={100} value={course.final_test_pass_mark} onChange={(e) => update("final_test_pass_mark", parseInt(e.target.value))}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold">Short description</label>
          <input value={course.short_description} onChange={(e) => update("short_description", e.target.value)}
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-semibold">Description</label>
          <textarea rows={5} value={course.description} onChange={(e) => update("description", e.target.value)}
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-semibold">Thumbnail URL</label>
          <div className="flex gap-2 mt-1">
            <input value={course.thumbnail_url} onChange={(e) => update("thumbnail_url", e.target.value)}
              className="flex-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm font-mono text-xs" />
            <button type="button" onClick={regenThumb} disabled={busy} data-testid="regen-thumb" className="btn-outline text-sm flex items-center gap-1">
              <ImageIcon className="w-4 h-4" /> Auto
            </button>
          </div>
          {course.thumbnail_url && (
            <img src={course.thumbnail_url} alt="" className="mt-2 w-40 aspect-[16/9] object-cover rounded-md border border-[#E5E7EB]" />
          )}
        </div>
        <div>
          <label className="text-sm font-semibold">Learning outcomes (one per line)</label>
          <textarea rows={4} value={(course.learning_outcomes || []).join("\n")}
            onChange={(e) => update("learning_outcomes", e.target.value.split("\n").filter(Boolean))}
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="pub" checked={course.is_published} onChange={(e) => update("is_published", e.target.checked)} />
          <label htmlFor="pub" className="text-sm font-semibold">Published</label>
        </div>
        <div className="flex justify-end">
          <button onClick={save} disabled={busy} data-testid="edit-save" className="btn-primary">{busy ? "Saving..." : "Save changes"}</button>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="font-display font-bold text-xl">Modules ({course.modules?.length || 0})</h2>
        <div className="mt-3 space-y-3">
          {course.modules?.map((m, i) => (
            <div key={m.id} className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold">Module {i+1}: {m.title}</div>
                <div className="text-sm text-[#4B5563]">{m.lessons.length} lessons · Quiz: {m.quiz?.questions?.length || 0} questions</div>
              </div>
              <button onClick={() => regenQuiz(m.id)} disabled={regen[m.id]}
                data-testid={`regen-quiz-${m.id}`}
                className="btn-outline text-sm flex items-center gap-1 shrink-0">
                {regen[m.id] ? <><Loader2 className="w-4 h-4 animate-spin" /> Regenerating…</> : <><Sparkles className="w-4 h-4" /> Regenerate Quiz</>}
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#4B5563] mt-3">Tip: Use AI Course Generator to create fully-populated courses, then regenerate individual quizzes or edit fields here.</p>
      </div>
    </div>
  );
}
